<script>
import { mapState, mapActions } from 'pinia';

import { contactSupport } from '@resources/js/api/users';
import { contactSupport as contactCampaignSupport } from '@resources/js/api/crowdpacs';
import { sendFeedback } from '@resources/js/api/campaignCoachChat';
import { supportCustomPricing } from '@resources/js/api/plans';
import { useUserStore } from '@store/useUserStore.js';
import { emailRegex, ErrorMessages } from '@resources/js/constants';

export default {
  name: 'CpContactSupportDialog',
  components: {
    CpButton: require('@resources/js/button').default,
    CpDialog: require('@resources/js/dialog').default,
    CpBoxContent: require('@resources/js/box-content').default,
    CpBox: require('@resources/js/box').default,
    CpToasted: require('@resources/js/toasted').default,
    CpTextInput: require('@resources/js/inputs/text').default,
  },
  directives: {
    margin: require('@resources/js/directives/margins').margin,
    marginLeft: require('@resources/js/directives/margins').marginLeft,
    marginRight: require('@resources/js/directives/margins').marginRight,
    marginBottom: require('@resources/js/directives/margins').marginBottom,
  },
  props: {
    text: {
      type: String,
      default: 'Support',
    },
    title: {
      type: String,
      default: 'Contact Support',
    },
    subtitle: {
      type: String,
      default: "Send us a message and we'll get back to you as soon as possible.",
    },
    defaultSubject: {
      type: String,
      default: '',
    },
    isSubjectDisabled: {
      type: Boolean,
      default: false,
    },
    messagePlaceholder: {
      type: String,
      default: 'Describe your request or issue',
    },
    customPricing: {
      type: Boolean,
      default: false,
    },
    isCampaign: {
      type: Boolean,
      default: false,
    },
    isCoach: {
      type: Boolean,
      default: false,
    },
    crowdpacId: {
      type: Number,
      default: null,
    },
    messageContext: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      showDialog: false,
      loading: false,
      toasted: [],
      subject: !!this.defaultSubject ? this.defaultSubject : '',
      message: '',
      replyEmail: '',
      subjectError: '',
      messageError: '',
      emailError: '',
      SUPPORT_SUBJECT_LENGTH: 75,
      SUPPORT_MESSAGE_LENGTH: 500,
    };
  },
  computed: {
    ...mapState(useUserStore, ['isUserLogin', 'userProfileData']),
    subjectLength() {
      return this.subject.length;
    },
    messageLength() {
      return this.message.length;
    },
    isSubjectValid() {
      return this.subject.length > 0 && this.subject.length <= this.SUPPORT_SUBJECT_LENGTH;
    },
    isMessageValid() {
      return this.message.length > 0 && this.message.length <= this.SUPPORT_MESSAGE_LENGTH;
    },
    isEmailValid() {
      if (this.isUserLogin) {
        return true;
      }
      return emailRegex.test(this.replyEmail);
    },
    isFormValid() {
      return this.isSubjectValid && this.isMessageValid && this.isEmailValid;
    },
  },
  watch: {
    showDialog(newVal) {
      if (newVal) {
        this.initializeEmail();
      } else {
        this.resetForm();
      }
    },
  },
  methods: {
    ...mapActions(useUserStore, ['getUserProfile']),
    handleDialog() {
      this.showDialog = !this.showDialog;
    },
    async initializeEmail() {
      if (this.isUserLogin && this.userProfileData?.email) {
        this.replyEmail = this.userProfileData.email;
      } else if (this.isUserLogin && !this.userProfileData) {
        await this.getUserProfile();
        if (this.userProfileData?.email) {
          this.replyEmail = this.userProfileData.email;
        }
      }
    },
    handleSendEmail() {
      this.clearErrors();

      if (!this.isFormValid) {
        if (!this.isSubjectValid) {
          this.subjectError = this.subject.length === 0 ? ErrorMessages.REQUIRED : ErrorMessages.SUPPORT_SUBJECT_LENGTH;
        }
        if (!this.isMessageValid) {
          this.messageError = this.message.length === 0 ? ErrorMessages.REQUIRED : ErrorMessages.SUPPORT_MESSAGE_LENGTH;
        }
        if (!this.isEmailValid) {
          this.emailError = ErrorMessages.VALID_EMAIL;
        }
        return;
      }
      const dataToSend = {
        email: this.replyEmail,
        subject: this.subject,
        message: this.message,
      };

      if (this.isCoach && this.messageContext) {
        dataToSend.message_context = this.messageContext;
      }

      this.loading = true;
      const apiCall = () => {
        if (this.isCoach) {
          return sendFeedback(this.crowdpacId, dataToSend);
        }
        if (this.isCampaign) {
          return contactCampaignSupport(this.crowdpacId, dataToSend);
        }
        if (this.customPricing) {
          return supportCustomPricing(dataToSend);
        }
        return contactSupport(dataToSend);
      };
      apiCall()
        .then(() => {
          this.createAlert({ message: 'Email sent successfully', theme: 'success' });
          this.resetForm();
          setTimeout(() => {
            this.loading = false;
            this.handleDialog();
          }, 3000);
        })
        .catch(error => {
          console.warn(error);
          this.createAlert({ message: error.message || 'Failed to send email. Please try again.', theme: 'danger' });
          this.loading = false;
        });
    },
    clearErrors() {
      this.subjectError = '';
      this.messageError = '';
      this.emailError = '';
    },
    resetForm() {
      this.subject = !!this.defaultSubject ? this.defaultSubject : '';
      this.message = '';
      if (!this.isUserLogin) {
        this.replyEmail = '';
      }
      this.clearErrors();
    },
    createAlert({ message = '', theme = 'danger', duration = 3000 }) {
      this.toasted.push({ key: `alert-${new Date().toString()}`, message, theme, duration });
    },
  },
};
</script>
<template>
  <span>
    <a @click.prevent="handleDialog" href="#" class="contact-support-link">{{ text }}</a>
    <CpDialog v-if="showDialog" @dismiss="handleDialog">
      <div class="alerts">
        <CpToasted v-for="toast in toasted" :key="toast.key" :message="toast.message" :theme="toast.theme" :duration="toast.duration" />
      </div>
      <CpBox>
        <div class="contactSupportContainer">
          <i @click.prevent="handleDialog" class="fal fa-times fa-1-5x black-color close-icon"></i>
          <div class="contactSupportData">
            <h2 class="heading2 black-color text-center">{{ title }}</h2>
            <p class="body black-color text-center">{{ subtitle }}</p>
          </div>

          <div class="contactSupportForm">
            <div class="form-field">
              <h4 class="subhead black-color text-left" v-margin-bottom="0">Email address</h4>
              <CpTextInput v-if="!isUserLogin" v-model="replyEmail" type="email" block placeholder="your.email@example.com" :error="emailError" />
              <CpTextInput v-else :value="replyEmail" type="email" block disabled />
            </div>
            <div class="form-field">
              <h4 class="subhead black-color text-left" v-margin-bottom="0">Subject of inquiry</h4>
              <CpTextInput
                v-model="subject"
                block
                placeholder="Enter subject"
                :error="subjectError"
                :maxlength="this.SUPPORT_SUBJECT_LENGTH"
                :disabled="isSubjectDisabled"
              />
              <span class="character-count caption neutral-2-color">{{ subjectLength }}/{{ this.SUPPORT_SUBJECT_LENGTH }}</span>
            </div>
            <div class="form-field">
              <h4 class="subhead black-color text-left" v-margin-bottom="0">Details of inquiry</h4>
              <CpTextInput
                v-model="message"
                multiline
                :rows="6"
                block
                :placeholder="messagePlaceholder"
                :error="messageError"
                :maxlength="this.SUPPORT_MESSAGE_LENGTH"
              />
              <span class="character-count caption neutral-2-color">{{ messageLength }}/{{ this.SUPPORT_MESSAGE_LENGTH }}</span>
              <p v-if="isCampaign" class="body neutral-2-color">
                Your email will include a link to your campaign so that our support staff can address your specific needs.
              </p>
            </div>
          </div>

          <div class="contactSupportButtons">
            <CpButton @click="handleDialog" block noborder :disabled="loading">Cancel</CpButton>
            <CpButton @click="handleSendEmail" block primary :loading="loading" :disabled="loading">Send</CpButton>
          </div>
        </div>
      </CpBox>
    </CpDialog>
  </span>
</template>
<style scoped>
a {
  color: unset;
}
.contactSupportContainer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px;
  position: relative;
}
.close-icon {
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;
}
.contactSupportData,
.contactSupportForm,
.form-field {
  display: flex;
  flex-direction: column;
}
.contactSupportForm {
  padding-bottom: 20px;
}
.character-count {
  text-align: right;
  margin-top: 0.2em;
}
.contactSupportButtons {
  display: flex;
  justify-content: center;
  gap: 10px;
}
:deep(.button__content) {
  padding: 0.6em 1em !important;
  margin-right: 0 !important;
}
</style>
